import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';
import { Store } from '../../Store';
import { getError } from '../../utils';
import { api } from '../../Api';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: '15d', label: '15 days' },
  { key: '30d', label: '1 month' },
  { key: '90d', label: '3 months' },
];

const money = (n) => '৳ ' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const num = (n) => Number(n || 0).toLocaleString();

/** Streams an authenticated export through a blob so it saves as a real file
 * (a plain <a href> can't carry the Authorization header the API needs). */
async function downloadReport(url, token, filename) {
  const { data } = await axios.get(url, {
    headers: { authorization: `Bearer ${token}` },
    responseType: 'blob',
  });
  const blobUrl = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}

export default function SalesReportScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [period, setPeriod] = useState('30d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState('units');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [exporting, setExporting] = useState('');

  const buildParams = (forExport) => {
    const params = new URLSearchParams();
    if (from || to) {
      if (from) params.set('from', from);
      if (to) params.set('to', to);
    } else {
      params.set('period', period);
    }
    if (q.trim()) params.set('q', q.trim());
    params.set('sortBy', sortBy);
    params.set('sortDir', sortDir);
    if (!forExport) {
      params.set('page', page);
      params.set('pageSize', 50);
    }
    return params;
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(api + `/api/reports/sales?${buildParams(false).toString()}`, {
        headers: { authorization: `Bearer ${userInfo.token}` },
      });
      setResult(data);
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, from, to, q, sortBy, sortDir, page]);

  const search = (value) => {
    setPage(1);
    setQ(value);
  };

  const toggleSort = (key) => {
    setPage(1);
    if (sortBy === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const sortIcon = (key) =>
    sortBy === key ? (
      <i className={sortDir === 'desc' ? 'fa fa-sort-down' : 'fa fa-sort-up'} aria-hidden="true" />
    ) : (
      <i className="fa fa-sort text-muted" aria-hidden="true" style={{ opacity: 0.4 }} />
    );

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      const url = api + `/api/reports/sales/export.${kind}?${buildParams(true).toString()}`;
      await downloadReport(url, userInfo.token, `sales-report.${kind}`);
    } catch (err) {
      setError(getError(err));
    } finally {
      setExporting('');
    }
  };

  const rows = result?.rows || [];
  const totals = result?.totals;

  return (
    <Container className="py-3">
      <Helmet><title>Sales Report</title></Helmet>

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div>
          <h3 className="mb-0"><i className="fa fa-line-chart me-2" aria-hidden="true" />Sales Report</h3>
          <div className="text-muted" style={{ fontSize: 13 }}>Total product sales, searchable and sortable</div>
        </div>
        <Link to="/pm"><Button size="sm" variant="outline-secondary">Back to Reports</Button></Link>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col xs={12} md="auto">
              <div className="btn-group" role="group">
                {PERIODS.map((p) => (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={!from && !to && period === p.key ? 'primary' : 'outline-secondary'}
                    onClick={() => { setFrom(''); setTo(''); setPeriod(p.key); setPage(1); }}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0" style={{ fontSize: 12 }}>From</Form.Label>
              <Form.Control size="sm" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0" style={{ fontSize: 12 }}>To</Form.Label>
              <Form.Control size="sm" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="mb-0" style={{ fontSize: 12 }}>Search product</Form.Label>
              <Form.Control size="sm" placeholder="Product name…" value={q} onChange={(e) => search(e.target.value)} />
            </Col>
            <Col xs="auto">
              {(from || to || q) && (
                <Button size="sm" variant="link" onClick={() => { setFrom(''); setTo(''); setQ(''); setPeriod('30d'); setPage(1); }}>
                  Clear
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div style={{ fontWeight: 600 }}>
            {totals ? `${num(totals.products)} products · ${num(totals.units)} units · ${money(totals.revenue)}` : 'Products'}
          </div>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-success" disabled={!!exporting} onClick={() => doExport('csv')}>
              {exporting === 'csv' ? <><span className="spinner-border spinner-border-sm me-1" /> CSV…</> : <><i className="fa fa-file-excel-o me-1" aria-hidden="true" />CSV</>}
            </Button>
            <Button size="sm" variant="outline-danger" disabled={!!exporting} onClick={() => doExport('pdf')}>
              {exporting === 'pdf' ? <><span className="spinner-border spinner-border-sm me-1" /> PDF…</> : <><i className="fa fa-file-pdf-o me-1" aria-hidden="true" />PDF</>}
            </Button>
          </div>
        </Card.Header>

        {loading ? <div className="p-4"><LoadingBox /></div> : error ? <div className="p-3"><MessageBox variant="danger">{error}</MessageBox></div> : (
          <>
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th role="button" onClick={() => toggleSort('name')}>Product {sortIcon('name')}</th>
                  <th role="button" className="text-end" onClick={() => toggleSort('units')}>Units {sortIcon('units')}</th>
                  <th role="button" className="text-end" onClick={() => toggleSort('orders')}>Orders {sortIcon('orders')}</th>
                  <th className="text-end">Avg Price</th>
                  <th role="button" className="text-end" onClick={() => toggleSort('revenue')}>Revenue {sortIcon('revenue')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.slug || i}>
                    <td>{(result.page - 1) * result.pageSize + i + 1}</td>
                    <td>{r.name}</td>
                    <td className="text-end">{num(r.units)}</td>
                    <td className="text-end">{num(r.orders)}</td>
                    <td className="text-end">{money(r.avgPrice)}</td>
                    <td className="text-end fw-semibold">{money(r.revenue)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">No sales match this filter.</td></tr>
                )}
              </tbody>
            </Table>

            {result && result.pages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-2">
                <Button size="sm" variant="outline-secondary" disabled={result.page <= 1} onClick={() => setPage(result.page - 1)}>
                  <i className="fa fa-chevron-left" aria-hidden="true" /> Prev
                </Button>
                <span className="text-muted" style={{ fontSize: 13 }}>Page {result.page} of {result.pages}</span>
                <Button size="sm" variant="outline-secondary" disabled={result.page >= result.pages} onClick={() => setPage(result.page + 1)}>
                  Next <i className="fa fa-chevron-right" aria-hidden="true" />
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </Container>
  );
}
